import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Keyboard } from 'react-native';
import Toast from 'react-native-root-toast';

import {
	MODIFY_PLANOGRAM_ITEM_CAPACITY,
	MODIFY_PLANOGRAM_ITEM_PARLEVEL,
	MODIFY_PLANOGRAM_ITEM_PRICE,
	MODIFY_PLANOGRAM_ITEM_PRODUCT,
	MODIFY_PLANOGRAM_ITEM_QUANTITY,
} from '../../../common/graphql/planogramItem';
import { CHANGE_TYPES } from '../../../common/constants';
import { shouldApplyDynamicParlevels } from '../../../common/utils/planograms/dynamicParlevels';
import { valueExists } from '../../../common/utils/utils';
import { resolveEditableNumericValue } from './PointOfSalePlanogramHelpers';

// Draft, validation and save for the market item edit dialog; only changed fields are sent.
const usePointOfSaleMarketItemEditor = ({
	pointOfSale,
	sortedItems,
	canModifyMarketItems,
	planogramEditDisabled,
	isCapacityEnabled,
	refetch,
}) => {
	const [editingMarketItemId, setEditingMarketItemId] = useState(null);
	const [marketItemDraft, setMarketItemDraft] = useState(null);
	const [isSavingMarketItemDialog, setIsSavingMarketItemDialog] = useState(false);
	const [modifyPlanogramItemProductMutation] = useMutation(MODIFY_PLANOGRAM_ITEM_PRODUCT);
	const [modifyPlanogramItemPriceMutation] = useMutation(MODIFY_PLANOGRAM_ITEM_PRICE);
	const [modifyPlanogramItemQuantityMutation] = useMutation(MODIFY_PLANOGRAM_ITEM_QUANTITY);
	const [modifyPlanogramItemParlevelMutation] = useMutation(MODIFY_PLANOGRAM_ITEM_PARLEVEL);
	const [modifyPlanogramItemCapacityMutation] = useMutation(MODIFY_PLANOGRAM_ITEM_CAPACITY);

	const editingMarketItem = useMemo(() => {
		if (!editingMarketItemId) {
			return null;
		}

		return sortedItems.find(item => item.id === editingMarketItemId) || null;
	}, [editingMarketItemId, sortedItems]);

	const closeMarketItemDialog = useCallback(() => {
		setEditingMarketItemId(null);
		setMarketItemDraft(null);
		setIsSavingMarketItemDialog(false);
	}, []);

	const openMarketItemDialog = useCallback((item) => {
		if (!canModifyMarketItems || !item?.id) {
			return;
		}

		setEditingMarketItemId(item.id);
		setMarketItemDraft({
			product: item.product || null,
			price: resolveEditableNumericValue(item.price, { fallback: 0 }),
			quantity: resolveEditableNumericValue(item.quantity, { fallback: 0 }),
			parlevel: resolveEditableNumericValue(item.parlevel, { fallback: 0 }),
			capacity: resolveEditableNumericValue(item.capacity, { allowNull: true }),
		});
	}, [canModifyMarketItems]);

	const updateMarketItemDraftValue = useCallback((field) => (value) => {
		setMarketItemDraft(previousValue => ({
			...(previousValue || {}),
			[field]: value,
		}));
	}, []);

	const updateMarketItemDraftBoundedValue = useCallback((field, {
		allowNull = false,
	} = {}) => (value) => {
		const resolvedValue = resolveEditableNumericValue(value, { fallback: 0, allowNull });
		if (valueExists(resolvedValue) && resolvedValue > 99) {
			return;
		}

		setMarketItemDraft(previousValue => ({
			...(previousValue || {}),
			[field]: resolvedValue,
		}));
	}, []);

	const updateMarketItemQuantity = useMemo(() => updateMarketItemDraftBoundedValue('quantity'), [updateMarketItemDraftBoundedValue]);
	const updateMarketItemParlevel = useMemo(() => updateMarketItemDraftBoundedValue('parlevel'), [updateMarketItemDraftBoundedValue]);
	const updateMarketItemCapacity = useMemo(() => updateMarketItemDraftBoundedValue('capacity', { allowNull: true }), [updateMarketItemDraftBoundedValue]);

	const saveMarketItemChanges = useCallback(async () => {
		if (!canModifyMarketItems || !editingMarketItem || !marketItemDraft) {
			return;
		}

		Keyboard.dismiss();

		const hasPriceBook = Boolean(editingMarketItem?.priceBook);
		const priceLocked = Boolean(editingMarketItem?.priceBookLocked || editingMarketItem?.priceBookApplied || hasPriceBook);
		const isParlevelLocked = shouldApplyDynamicParlevels(editingMarketItem, pointOfSale);
		const currentPrice = resolveEditableNumericValue(editingMarketItem.price, { fallback: 0 });
		const currentQuantity = resolveEditableNumericValue(editingMarketItem.quantity, { fallback: 0 });
		const currentParlevel = resolveEditableNumericValue(editingMarketItem.parlevel, { fallback: 0 });
		const currentCapacity = resolveEditableNumericValue(editingMarketItem.capacity, { allowNull: true });
		const nextPrice = resolveEditableNumericValue(marketItemDraft.price, { fallback: currentPrice });
		const nextQuantity = resolveEditableNumericValue(marketItemDraft.quantity, { fallback: currentQuantity });
		const nextParlevel = resolveEditableNumericValue(marketItemDraft.parlevel, { fallback: currentParlevel });
		const nextCapacity = resolveEditableNumericValue(marketItemDraft.capacity, { fallback: currentCapacity, allowNull: true });
		const currentProductId = editingMarketItem.product?.id || null;
		const nextProductId = marketItemDraft.product?.id || null;
		const shouldUpdateProduct = !planogramEditDisabled && nextProductId !== currentProductId;
		const shouldUpdatePrice = !planogramEditDisabled && !priceLocked && nextPrice !== currentPrice;
		const shouldUpdateParlevel = !planogramEditDisabled && !isParlevelLocked && nextParlevel !== currentParlevel;
		const shouldUpdateCapacity = !planogramEditDisabled && isCapacityEnabled && nextCapacity !== currentCapacity;
		const hasChanges = (
			shouldUpdateProduct ||
			shouldUpdatePrice ||
			nextQuantity !== currentQuantity ||
			shouldUpdateParlevel ||
			shouldUpdateCapacity
		);

		if (!hasChanges) {
			Toast.show('No changes to save.', {
				duration: Toast.durations.SHORT,
			});
			closeMarketItemDialog();
			return;
		}

		setIsSavingMarketItemDialog(true);
		try {
			if (shouldUpdateProduct) {
				await modifyPlanogramItemProductMutation({
					variables: {
						id: editingMarketItem.id,
						values: {
							productId: nextProductId,
							productChangeType: CHANGE_TYPES.FORCE,
						},
					},
				});
			}

			if (shouldUpdatePrice) {
				await modifyPlanogramItemPriceMutation({
					variables: {
						id: editingMarketItem.id,
						values: {
							price: nextPrice,
							priceChangeType: CHANGE_TYPES.FORCE,
						},
					},
				});
			}

			if (nextQuantity !== currentQuantity) {
				await modifyPlanogramItemQuantityMutation({
					variables: {
						id: editingMarketItem.id,
						values: {
							quantity: nextQuantity,
						},
					},
				});
			}

			if (shouldUpdateParlevel) {
				await modifyPlanogramItemParlevelMutation({
					variables: {
						id: editingMarketItem.id,
						values: {
							parlevel: nextParlevel,
						},
					},
				});
			}

			if (shouldUpdateCapacity) {
				await modifyPlanogramItemCapacityMutation({
					variables: {
						id: editingMarketItem.id,
						values: {
							capacity: nextCapacity,
						},
					},
				});
			}

			await refetch();
			Toast.show('Planogram item updated', {
				duration: Toast.durations.SHORT,
			});
			closeMarketItemDialog();
		}
		catch (error) {
			Toast.show('Unable to save planogram item changes.', {
				duration: Toast.durations.SHORT,
			});
			closeMarketItemDialog();
		}
	}, [
		canModifyMarketItems,
		closeMarketItemDialog,
		editingMarketItem,
		isCapacityEnabled,
		planogramEditDisabled,
		marketItemDraft,
		modifyPlanogramItemCapacityMutation,
		modifyPlanogramItemParlevelMutation,
		modifyPlanogramItemPriceMutation,
		modifyPlanogramItemProductMutation,
		modifyPlanogramItemQuantityMutation,
		pointOfSale,
		refetch,
	]);

	return {
		editingMarketItem,
		marketItemDraft,
		isSavingMarketItemDialog,
		openMarketItemDialog,
		closeMarketItemDialog,
		updateMarketItemDraftValue,
		updateMarketItemQuantity,
		updateMarketItemParlevel,
		updateMarketItemCapacity,
		saveMarketItemChanges,
	};
};

export default usePointOfSaleMarketItemEditor;
